import React, { lazy } from 'react';
import { Route, Switch } from 'react-router-dom';
import AuthorizeRoute from './authentication/AuthorizeRoute';
import {
  ApplicationPaths,
  LoginActions,
  LogoutActions
} from './authentication/AuthorizationConstants';
import Login from './authentication/Login';
import Logout from './authentication/Logout';
// Lazy load the routes to support code splitting
const Home = lazy(() => import('./modules/home/components/Home'));
const CoreUI = lazy(() => import('./modules/core/components/CoreUI'));
const ControlPanelConfigurationRoutes = lazy(() =>
  import('./modules/configuration/controlpanel/components/ControlPanelConfigurationRoutes')
);
const GoalWorkspaceRoutes = lazy(() => import('./modules/goals/components/GoalWorkspaceRoutes'));
const ActivityBuilderRoutes = lazy(() =>
  import('./modules/learning/activityBuilder/components/ActivityBuilderRoutes')
);
const ActivityAssignments = lazy(() =>
  import('./modules/learning/activityAssignments/components/ActivityAssignments')
);
const ToDoListConfiguration = lazy(() =>
  import('./modules/configuration/activitiesAndTasks/components/MyActivitiesAndTasksConfiguration')
);
const ReportingRoutes = lazy(() => import('./modules/reporting/ReportingRoutes'));

const MyToDoList = lazy(() =>
  import('./modules/onePlace/activitiesAndTasks/activities/components/MyActivitiesRoutes')
);

const AccessDeniedPage = lazy(() => import('./common/layout/components/AccessDeniedPage'));

const UserManagement = lazy(() =>
  import('./modules/administration/users/components/UserManagement')
);
const OfferRoutes = lazy(() => import('./modules/recruiting/offer/components/route/OfferRoutes'));
const AllApplicants = lazy(() =>
  import('./modules/recruiting/allApplicants/components/AllApplicantsView')
);
const PensSettings = lazy(() => import('./modules/learning/pens/components/settings'));
const MicrosoftTeamsSettings = lazy(() =>
  import('./modules/learning/vilt/msTeams/components/msTeamsSettings')
);
const ZoomSettings = lazy(() => import('./modules/learning/vilt/zoom/components/zoomSettings'));
const IntegrationSettings = lazy(() =>
  import('./modules/learning/vivaLearning/components/integrationSettings')
);
const AccreditationRoutes = lazy(() =>
  import('./modules/learning/accreditation/components/AccreditationRoutes')
);
const LateCompletionsListRoutes = lazy(() =>
  import('./modules/learning/latecompletions/components/LateCompletionsListRoutes')
);

const AchievementTemplateRoutes = lazy(() =>
  import(
    './modules/administration/development/developmentplan/achievement/components/AchievementTemplateRoutes'
  )
);
const MarketComparisonRoutes = lazy(() =>
  import('./modules/marketComparison/components/MarketComparisonRoutes')
);

const CredlyConfiguration = lazy(() =>
  import('./modules/configuration/backPackServer/credly/components/CredlyConfiguration')
);
const AssignmentApprovalsRoutes = lazy(() =>
  import('./modules/learning/assignmentapprovals/components/AssignmentApprovalsList')
);
const QuestionBankRoutes = lazy(() =>
  import('./modules/learning/questionBanks/components/QuestionBankRoutes')
);
const InitiaivesSettings = lazy(() =>
  import('./modules/configuration/InitiativesSettings/components/initiativesSettings')
);
const DashboardManagerRoutes = lazy(() =>
  import('./modules/dashboards/components/DashboardManagerRoutes')
);
const DashboardRoutes = lazy(() => import('./modules/dashboards/components/DashboardRoutes'));
const ActivityQrAttendanceReport = lazy(() =>
  import('./modules/learning/activityQrAttendance/components/ActivityQrAttendanceReport')
);
const contentConsumerLaunchSetting = lazy(() =>
  import('./modules/learning/contentIntegrationLaunchSettings/components/ContentConsumerSetting')
);
const ViltAttendanceSyncWizard = lazy(() =>
  import('./modules/learning/vilt/attendanceSync/components/AttendanceSyncWizard')
);
function loginAction(name) {
  return <Login action={name} />;
}

function logoutAction(name) {
  return <Logout action={name} />;
}

const AppRoutes = () => {
  return (
    <Switch>
      {/* BEGIN - routes required for oidc-client SSO */}
      {/* Don't add any new routes above this block */}
      <Route exact path={ApplicationPaths.Login} render={() => loginAction(LoginActions.Login)} />
      <Route
        exact
        path={ApplicationPaths.LoginCallback}
        render={() => loginAction(LoginActions.LoginCallback)}
      />
      <Route
        exact
        path={ApplicationPaths.Logout}
        render={() => logoutAction(LogoutActions.Logout)}
      />
      {/* END - oidc-client SSO routes */}
      <AuthorizeRoute path="/accessDenied" component={AccessDeniedPage} />
      <AuthorizeRoute path="/accreditation" component={AccreditationRoutes} />
      <AuthorizeRoute path="/activitybuilder" component={ActivityBuilderRoutes} />
      <AuthorizeRoute path="/achtemplates" component={AchievementTemplateRoutes} />
      <AuthorizeRoute
        path="/admin/activity/:activityId(\d+)/:offeringTargetId(\d+)/assignments"
        component={ActivityAssignments}
      />
      <AuthorizeRoute
        path="/admin/activity/:activityId(\d+)/assignments"
        component={ActivityAssignments}
      />
      <AuthorizeRoute
        path="/controlpanelConfiguration"
        component={ControlPanelConfigurationRoutes}
      />
      <AuthorizeRoute path="/admin/jobs/marketComparison" component={MarketComparisonRoutes} />
      <AuthorizeRoute path="/assignmentapprovals" component={AssignmentApprovalsRoutes} />
      <AuthorizeRoute path="/credlySettings" component={CredlyConfiguration} />
      <AuthorizeRoute path="/goals" component={GoalWorkspaceRoutes} />
      <AuthorizeRoute path="/mylearningconfiguration" component={ToDoListConfiguration} />
      <AuthorizeRoute path="/reporting" component={ReportingRoutes} />
      <AuthorizeRoute path="/mylearning" component={MyToDoList} />
      <AuthorizeRoute path="/administration/users/userManagement" component={UserManagement} />
      <AuthorizeRoute path="/recruiting/offer" component={OfferRoutes} />
      <AuthorizeRoute path="/recruiting/allApplicants" component={AllApplicants} />
      <AuthorizeRoute path="/pens" component={PensSettings} />
      <AuthorizeRoute path="/msTeams" component={MicrosoftTeamsSettings} />
      <AuthorizeRoute path="/questionBanks" component={QuestionBankRoutes} />
      <AuthorizeRoute path="/zoom" component={ZoomSettings} />
      <AuthorizeRoute path="/learn/VivaLearnIntegrationSettings" component={IntegrationSettings} />
      <AuthorizeRoute path="/initiativesSettings" component={InitiaivesSettings} />
      <AuthorizeRoute path="/activityqrattendance/report" component={ActivityQrAttendanceReport} />
      <AuthorizeRoute
        path="/learn/consumerIntegrationLaunchSettings"
        component={contentConsumerLaunchSetting}
      />
      <AuthorizeRoute path="/dashboard" component={DashboardRoutes} />
      <AuthorizeRoute path="/dashboardManager" component={DashboardManagerRoutes} />
      <AuthorizeRoute path="/latecompletions" component={LateCompletionsListRoutes} />
      <AuthorizeRoute path="/vilt/attendanceSync" component={ViltAttendanceSyncWizard} />
      <AuthorizeRoute path="/c" component={CoreUI} />
      <AuthorizeRoute path="/" component={Home} />
    </Switch>
  );
};

export default AppRoutes;
